import { Link, Outlet, useLocation } from "react-router-dom";
import { StepsNavigation, type NavItem } from "../common/StepsNavigation";
import { StepsFooter } from "../common/StepsFooter";
import Header from "../Header";
import RightAside from "../RightAside";
import UserService from "../../services/UserService";
import { isProductAdmin } from "./access-control";
import { RefreshButton } from "./refresh-button";
import { TicketSizeToggle } from "./ticket-size-toggle";
import { TicketSizeProvider } from "../../pages/PortfolioPerformance/lib/ticket-size";

const basePath = "/portfolio-performance/net-investment";

// Every step is an independently reachable page (unlike Underwriting Engine's linear
// wizard), so navigation runs in StepsNavigation/StepsFooter's route mode -- no
// currentStepIndex/onStepChange, no nextBlocked, no isFirstStepHidden.
//
// Maps directly onto the design prototype's STEPS array (athena-portfolio-performance/
// src/components/step-shell.tsx): 6 top-level steps, 4 of which carry a lettered list of
// hidden sub-pages. There is no per-page role tiering in this product (nothing in its
// spec calls for reviewer/admin-only pages), so unlike RBPE's AppLayout this list is a
// module constant rather than built per-viewer. Manage Access itself IS gated (see the
// link row below and PortfolioPerformance.tsx) -- only no individual feature page is.
const navItems: NavItem[] = [
  {
    label: "Growth & Exposure",
    path: "/portfolio-performance/net-investment",
    children: [
      { label: "Industry Benchmark", path: "/portfolio-performance/industry-benchmark" },
      { label: "Exposure Tracking", path: "/portfolio-performance/exposure-tracking" },
    ],
  },
  {
    label: "Watchlist",
    path: "/portfolio-performance/watchlist",
    children: [
      { label: "APD Monitoring", path: "/portfolio-performance/apd-monitoring" },
      { label: "EPD", path: "/portfolio-performance/epd" },
      { label: "Macro Exposure", path: "/portfolio-performance/macro-exposure" },
    ],
  },
  {
    label: "Loss & Recovery",
    path: "/portfolio-performance/loss-recovery",
    children: [
      { label: "LGD", path: "/portfolio-performance/lgd" },
      { label: "Expected Loss", path: "/portfolio-performance/expected-loss" },
      {
        label: "Loss Calibration",
        path: "/portfolio-performance/loss-calibration",
      },
      { label: "Annualized Loss", path: "/portfolio-performance/annualized-loss" },
      { label: "Weighted Average Life", path: "/portfolio-performance/loss-development-wal" },
      {
        label: "Chargeoff Acceleration",
        path: "/portfolio-performance/chargeoff-acceleration",
      },
    ],
  },
  {
    label: "Earnings & Return",
    path: "/portfolio-performance/earnings-return",
    children: [
      {
        label: "Prepayment Projections",
        path: "/portfolio-performance/lifetime-prepayment-projections",
      },
      {
        label: "Repeat Customer Projections",
        path: "/portfolio-performance/lifetime-repeat-customer-projections",
      },
    ],
  },
  { label: "Snapshot", path: "/portfolio-performance/snapshot" },
  { label: "Attestation", path: "/portfolio-performance/attestation" },
];

// Pages whose data the Ticket Size toggle filters. The rest read tables with no deal-size dimension (LGD, EPD),
// embedded dashboards the app doesn't query (Snapshot), or no data at all (Attestation, Manage Access), so on those
// the toggle is shown disabled. Macro Exposure is scored per band since the ticket-size backfill; Mid-market and
// Large stay selectable there and show a no-data state while the scored book has none.
const TICKET_SIZE_PAGES = [
  "/portfolio-performance/net-investment",
  "/portfolio-performance/industry-benchmark",
  "/portfolio-performance/watchlist",
  "/portfolio-performance/apd-monitoring",
  "/portfolio-performance/macro-exposure",
  "/portfolio-performance/loss-recovery",
  "/portfolio-performance/expected-loss",
  "/portfolio-performance/loss-calibration",
  "/portfolio-performance/annualized-loss",
  "/portfolio-performance/loss-development-wal",
  "/portfolio-performance/chargeoff-acceleration",
  "/portfolio-performance/earnings-return",
  "/portfolio-performance/lifetime-prepayment-projections",
  "/portfolio-performance/lifetime-repeat-customer-projections",
];

export function AppLayout() {
  const token = UserService.getToken();
  const showManageAccess = isProductAdmin();
  const { pathname } = useLocation();
  const ticketSizeApplies = TICKET_SIZE_PAGES.some((page) => pathname.startsWith(page));

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header
        token={token}
        isMarketplacePage={false}
        notificationsCount={0}
        refreshNotifications={() => {}}
        setIndustry={() => {}}
        setCompany={() => {}}
        leftNode={null}
        productLabel="Portfolio Performance"
        fixed={true}
      />
      <StepsNavigation navItems={navItems} basePath={basePath} />

      <TicketSizeProvider>
        <main className="flex-1 p-6 animate-fade-in pt-[200px] pb-[80px]">
          {/* Same right-aligned row risk-pricing-engine puts at the top of its own main content: the
              Ticket Size toggle on every page, with Manage Access alongside it for admins. Refresh sits first. */}
          <div className="flex items-center justify-end gap-6 mb-3">
            <RefreshButton />
            <TicketSizeToggle disabled={!ticketSizeApplies} />
            {showManageAccess && (
              <Link
                to="/portfolio-performance/manage-access"
                className="text-xs font-medium text-amber hover:underline"
              >
                Manage Access →
              </Link>
            )}
          </div>
          <Outlet />
        </main>
      </TicketSizeProvider>

      <StepsFooter navItems={navItems} basePath={basePath} />
      <RightAside showChildrenWhenNoPanel={false}>{null}</RightAside>
    </div>
  );
}
